import { Injectable, inject } from '@angular/core';

import { Fai, Pto } from '@bytel/bytel-sales';
import { CheckoutStorageService } from '@bytel/checkout';

import { Oauth2ResourcesService } from '@base/common/oauth2/oauth2-resources.service';

import { Observable } from 'rxjs';

import { CorbisCreateCartBody, CorbisUpdateCartBody, ICorbisCartApiResponse } from '../interfaces/api/corbis.interface';
import {
    IEligibility,
    IEligibilityResult,
    IFaiAddressInstallation,
    IFaiBoxResult,
    IFaiData,
} from '../interfaces/fai.interface';
import { MessageService } from '../services/message.service';
import { ErrorModalComponent } from '../shared/error-modal/error-modal.component';

@Injectable({
    providedIn: 'root',
})
export class FaiRepository {
    protected checkoutStorage: CheckoutStorageService = inject(CheckoutStorageService);
    private _storageKey: string = 'fai';
    private messageService: MessageService = inject(MessageService);
    private oauth2Resource: Oauth2ResourcesService = inject(Oauth2ResourcesService);

    public save(): void {
        const faiData = {
            eligibility4g: this.checkoutStorage.get('eligibility_tokyo', false),
            userAddress: this._getUserInstallAddress(),
            eligibility: this._getUpdatedEligData(),
        };

        if (Object.values(faiData).every((value) => !value)) {
            return;
        }

        this.checkoutStorage.set(this._storageKey, faiData);
    }

    public getFaiData(): IFaiData {
        return this.checkoutStorage.get(this._storageKey);
    }

    public getEligibilityTokyo(): IFaiBoxResult {
        return this.getFaiData()?.eligibility4g;
    }

    public getEligAddress(): IFaiAddressInstallation {
        return this.getFaiData()?.userAddress;
    }

    public getEligibility(): IEligibility {
        const result = this.getFaiData()?.eligibility;
        let eligibility: IEligibility;
        if (result) {
            this._checkUndefinedAddress(result);
            eligibility = {
                cartId: result.idPanier,
                address: result.technicalAddress,
                shouldBypass: result.results?.ftth?.shouldBypass ?? false,
                pto: result.results.ftth.pto,
                buildings: result.results?.ftth.buildings,
                hasFTTHEligibility: result.results?.ftth?.status,
                isCdl:
                    (result.results.xdsl && result.housing?.accessStatusDSL?.toLowerCase() === 'inexistant') || false,
                technicalInfos: [
                    {
                        type: 'groupeCategorieRaccoLogement',
                        valeur: result.results?.ftth?.groupeCategorieRaccoLogement,
                    },
                    {
                        type: 'type_logement',
                        valeur: result.results?.ftth?.housingType,
                    },
                    {
                        type: 'typeEmplacementPM',
                        valeur: result.results?.ftth?.pmLocationType,
                    },
                ],
            };
        }

        // TODO: gestion des débits pour toute les techno ?
        if (result?.results?.xdsl) {
            eligibility.xDSLtechnoSpeed = {
                speedUp: result.results.xdsl.speedUp,
                speedDown: result.results.xdsl.speedDown,
            };
        }

        if (result?.results?.box4g || result?.results?.box5g) {
            eligibility.homeZone = {
                box4g: result.results.box4g?.homeZone,
                box5g: result.results.box5g?.homeZone,
            };
        }

        return eligibility;
    }

    public putEligibilityAddress(idPanier: string, address: Pto): Observable<any> {
        return this.oauth2Resource.eligibilite(idPanier).techno(Fai.TECHNOLOGIES.ADSL).put({
            adresse: address,
        });
    }

    public getCorbisCart(cartId: string): Observable<ICorbisCartApiResponse> {
        return this.oauth2Resource.getCorbisCart(cartId).useCorbisApi().get();
    }

    public createCorbisCart(data: CorbisCreateCartBody): Observable<{ idPanier: string }> {
        return this.oauth2Resource.createCorbisCart().useCorbisApi().post(data);
    }

    public updateCorbisCart(cartId: string, data: CorbisUpdateCartBody): Observable<void> {
        return this.oauth2Resource.updateCorbisCart(cartId).useCorbisApi('4', 'ModificationPanierCorbis').put(data);
    }

    private _checkUndefinedAddress(result: IEligibilityResult): void {
        if (!Object.keys(result?.technicalAddress?.city).length) {
            this.messageService.openModal(ErrorModalComponent);
        }
    }

    private _getUpdatedEligData(): IEligibilityResult {
        const newEligData = this.checkoutStorage.get('provider_results', false);
        const currentEligData: IFaiData = this.checkoutStorage.get(this._storageKey, true);

        if (!newEligData && currentEligData?.eligibility) {
            return currentEligData.eligibility;
        } else if (!currentEligData?.eligibility) {
            return newEligData;
        } else {
            return newEligData?.idPanier !== currentEligData?.eligibility?.idPanier
                ? newEligData
                : currentEligData?.eligibility;
        }
    }

    private _getUserInstallAddress(): IFaiAddressInstallation {
        const result = this.getFaiData()?.eligibility;
        if (result) {
            return {
                codeInsee: result.technicalAddress.city.INSEECode,
                codePostal: result.technicalAddress.zipCode,
                codeVoie: result.technicalAddress.street.label,
                commune: result.technicalAddress.city.label,
                listeRouge: false,
                numeroVoie: result.technicalAddress.streetNumber.number,
                voie: result.technicalAddress.street.label,
                complementVoie: result.technicalAddress.streetNumber.complement,
            };
        }

        return undefined;
    }
}
